import React, { useCallback, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import { useNotification } from '../../../hooks/useNotification';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { CreateFormContact } from '../../../components/contact/CreateFormContact';
import { useForm } from '../../../hooks/useForm';
import { DialogSelectCompany } from '../../../components/dialogs/DialogSelectCompany';
import LoadingOverlay from '../../../components/others/LoadingOverlay';
import { editContact, fetchContactById, insertContact } from '../../../services/contact.service';
import { getCompanies } from '../../../services/approval.service';
import { ActionButtons } from '../../../components/others/ActionButtons';
import { useTranslation } from 'react-i18next';

export const LayoutContact = () => {
    const { t } = useTranslation();
    const [sendNotification] = useNotification();
    const navigate = useNavigate();
    const [{ userSession }] = useDataAuth();
    const location = useLocation();
    const { config } = location.state || {};
    const initialState = {
        id_company: '',
        name: '',
        last_name: '',
        title: '',
        area: '',
        phone: '',
        extension: '',
        cellphone: '',
        email: '',
        country: '',
        state: '',
        city: '',
        address: '',
        origin: '',
        segmentation: '',
        role: '',
        relationship_status: '',
        id_user_owner: 0,
        clients: []
    };

    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(initialState);
    const [loading, setLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isView, setIsView] = useState(false);

    const [openDialogCompany, setOpenDialogCompany] = useState(false);
    const [companies, setCompanies] = useState([]);
    const formContact = useRef();

    const fetchDataContact = useCallback(async (id) => {
        setIsEdit(true);
        setLoading(true);
        try {
            const response = await fetchContactById(userSession.token, id);
            if (response.status === 200) {
                const dto = response.data;
                dto.clients = response.data.clients ? response.data.clients.split(',').filter(c => c) : [];
                setAllInfo(dto);
            }
        } catch (error) {
            console.error("Error fetching contact data:", error);
        } finally {
            setLoading(false);
        }
    }, [userSession.token]);

    useEffect(() => {
        if (location.state?.config) {
            const { config } = location.state;
            if (config.data?.work_flow === 'FLOW_PROCESS-VIEW_CONTACT') {
                setIsView(true);
            }
            if (config.data?.id) {
                fetchDataContact(config.data?.id);
                setIsEdit(true);
            } else {
                const fetchCompanies = async () => {
                    try {
                        const response = await getCompanies('60', userSession.token);
                        setCompanies(response.data);
                        if (response.data && response.data.length === 1) {
                            const dto = response.data[0];
                            handleDirectInputChange('id_company', dto.id_company);
                            setOpenDialogCompany(false);
                        } else {
                            setOpenDialogCompany(true);
                        }
                    } catch (e) {
                        console.log(e.message);
                    } finally {
                        setLoading(false);
                    }
                };
                fetchCompanies();
            }
        }
    }, [fetchDataContact, location.state, userSession.token]);

    const saveData = async (data) => {
        let response
        if (isEdit) {
            response = await editContact(userSession.token, data);
        } else {
            response = await insertContact(userSession.token, data);
        }
        if (response && response.data[0]?.result === "EXIST") {
            sendNotification({
                msg: t("contact.alreadyExists"),
                variant: "info",
            });
            return false;
        }
        sendNotification({
            msg: t("messages.saveSuccess"),
            variant: "success",
        });
        Cancel();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const isValidFormContact = formContact.current?.validateForm();
        const data = JSON.stringify({ ...values, id_contact: config.data?.id ?? 0 });
        if (isValidFormContact) {
            saveData(data);
        } else {
            sendNotification({
                msg: t("validation.requiredFieldsMissing"),
                variant: "error",
            });
        }
    };

    const Cancel = () => navigate(-1);

    const handleConfirmAction = useCallback(async (dto) => {
        handleDirectInputChange("id_company", dto.id_company);
        setOpenDialogCompany(false);
    }, []);

    return (
        <Box sx={{ mx: 'auto' }}>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        {t("contact.contactTitle")}
                    </Typography>
                    <CreateFormContact
                        ref={formContact}
                        dto={values}
                        handleInputChange={handleInputChange}
                        handleDirectInputChange={handleDirectInputChange}
                        isEdit={isEdit}
                        isView={isView}
                    />
                    <ActionButtons
                        onCancel={Cancel}
                        onSubmit={handleSubmit}
                        disabledSubmit={isView}
                    />
                </CardContent>
            </Card>
            <DialogSelectCompany
                openDialogConfirm={openDialogCompany}
                handleCancelConfirm={Cancel}
                handleConfirmAction={handleConfirmAction}
                data={companies}
            />
            <LoadingOverlay isOpen={loading} />
        </Box>
    );
};